import {
  subMonths,
  addMonths,
  subYears,
  addYears,
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  isEqual,
  subMilliseconds,
  addMilliseconds,
  startOfSecond,
  startOfMinute,
  startOfHour,
  endOfSecond,
  endOfMinute,
  endOfHour,
  differenceInDays,
  differenceInWeeks,
  differenceInMonths,
  differenceInYears,
  differenceInSeconds,
  differenceInMinutes,
  differenceInHours,
  differenceInMilliseconds,
} from 'date-fns';

type TimeUnit =
  | 'days'
  | 'weeks'
  | 'months'
  | 'years'
  | 'milliseconds'
  | 'seconds'
  | 'minutes'
  | 'hours';

const startOfMap = new Map<TimeUnit, (d: Date) => Date>([
  ['days', startOfDay],
  ['weeks', startOfWeek],
  ['months', startOfMonth],
  ['years', startOfYear],
  ['seconds', startOfSecond],
  ['minutes', startOfMinute],
  ['hours', startOfHour],
]);

const endOfMap = new Map<TimeUnit, (d: Date) => Date>([
  ['days', endOfDay],
  ['weeks', endOfWeek],
  ['months', endOfMonth],
  ['years', endOfYear],
  ['seconds', endOfSecond],
  ['minutes', endOfMinute],
  ['hours', endOfHour],
]);

const durationMap = new Map<TimeUnit, (d1: Date, d2: Date) => number>([
  ['days', differenceInDays],
  ['weeks', differenceInWeeks],
  ['months', differenceInMonths],
  ['years', differenceInYears],
  ['milliseconds', differenceInMilliseconds],
  ['seconds', differenceInSeconds],
  ['minutes', differenceInMinutes],
  ['hours', differenceInHours],
]);

export class DateInterval {
  private cache = { isWhole: {}, durationAs: {} };

  constructor(readonly begin: Date, readonly end: Date) {}

  get isWholeDay() {
    return this.isWhole('days');
  }

  get isWholeMonth() {
    return this.isWhole('months');
  }

  get isWholeYear() {
    return this.isWhole('years');
  }

  get durationInMillis() {
    return this.durationAs('milliseconds');
  }

  get durationInHours() {
    return this.durationAs('hours');
  }

  get durationInDays() {
    return this.durationAs('days');
  }

  get durationInMonths() {
    return this.durationAs('months');
  }

  get durationInYears() {
    return this.durationAs('years');
  }

  get isFullDays() {
    return (
      isEqual(startOfDay(this.begin), this.begin) &&
      isEqual(endOfDay(this.end), this.end)
    );
  }

  get isFullWeek() {
    return (
      isEqual(startOfWeek(this.begin), this.begin) &&
      isEqual(endOfWeek(this.end), this.end)
    );
  }

  get isFullMonths() {
    return (
      isEqual(startOfMonth(this.begin), this.begin) &&
      isEqual(endOfMonth(this.end), this.end)
    );
  }

  get isFullYears() {
    return (
      isEqual(startOfYear(this.begin), this.begin) &&
      isEqual(endOfYear(this.end), this.end)
    );
  }

  withBegin(date: Date) {
    return DateInterval.of(date, this.end);
  }

  withEnd(date: Date) {
    return DateInterval.of(this.begin, date);
  }

  next(): DateInterval {
    if (this.isWholeMonth) {
      return DateInterval.ofMonth(addMonths(this.begin, 1));
    } else if (this.isWholeYear) {
      return DateInterval.ofYear(addYears(this.begin, 1));
    } else if (this.isFullMonths) {
      return DateInterval.of(
        startOfMonth(addMonths(this.end, 1)),
        endOfMonth(addMonths(this.end, this.durationInMonths + 1))
      );
    } else {
      const begin = addMilliseconds(this.end, 1);
      return new DateInterval(
        begin,
        addMilliseconds(begin, this.durationInMillis)
      );
    }
  }

  previous(): DateInterval {
    if (this.isWholeMonth) {
      return DateInterval.ofMonth(subMonths(this.begin, 1));
    } else if (this.isWholeYear) {
      return DateInterval.ofYear(subYears(this.begin, 1));
    } else if (this.isFullMonths) {
      return DateInterval.of(
        startOfMonth(subMonths(this.begin, this.durationInMonths + 1)),
        endOfMonth(subMonths(this.begin, 1))
      );
    } else {
      const end = subMilliseconds(this.begin, 1);
      return new DateInterval(subMilliseconds(end, this.durationInMillis), end);
    }
  }

  toString(): string {
    return `${this.parseDate(this.begin)}--${this.parseDate(this.end)}`;
  }

  protected parseDate(date: Date): string {
    return `${date.getUTCFullYear()}-${
      date.getUTCMonth() + 1
    }-${date.getUTCDate()}T${date.getUTCHours()}:${date.getUTCMinutes()}:${date.getUTCSeconds()}Z`;
  }

  private durationAs(value: TimeUnit) {
    if (this.cache.durationAs[value]) {
      return this.cache.durationAs[value];
    }
    const duration = durationMap.get(value);
    return (this.cache.durationAs[value] = duration(this.begin, this.end));
  }

  private isWhole(value: TimeUnit) {
    if (this.cache.isWhole[value]) {
      return this.cache.isWhole[value];
    }
    const startOf = startOfMap.get(value);
    const endOf = endOfMap.get(value);
    return (this.cache.isWhole[value] =
      isEqual(startOf(this.begin), this.begin) &&
      isEqual(endOf(this.begin), this.end));
  }

  static ofToday(): DateInterval {
    return this.ofDay(new Date());
  }

  static ofDay(date: Date): DateInterval {
    return new DateInterval(startOfDay(date), endOfDay(date));
  }

  static ofWeek(date: Date): DateInterval {
    return new DateInterval(startOfWeek(date), endOfWeek(date));
  }

  static ofMonth(date: Date): DateInterval {
    return new DateInterval(startOfMonth(date), endOfMonth(date));
  }

  static ofYear(date: Date): DateInterval {
    return new DateInterval(startOfYear(date), endOfYear(date));
  }

  static of(begin: Date, end: Date): DateInterval {
    return new DateInterval(startOfDay(begin), endOfDay(end));
  }
}
